import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtMon, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon } from "@phosphor-icons/react";
import { CNTable, CNViewer, Modal, Lbl } from "./_credit/SalesCreditNotes";

/**
 * Notas de crédito de COMPRA (proveedores). Las notas de crédito de VENTA se gestionan
 * únicamente en Ventas y facturación → Notas de crédito.
 */
export default function CreditNotes() {
  const [purchaseCNs, setPurchaseCNs] = useState([]);
  const [openPurchase, setOpenPurchase] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/purchase-credit-notes");
    setPurchaseCNs(data || []);
  };
  useEffect(() => { load(); }, []);

  const voidCn = async (cn) => {
    if (!window.confirm(`¿Anular ${cn.numero}? Se revertirá el efecto sobre factura e inventario.`)) return;
    try { await api.post(`/purchase-credit-notes/${cn.id}/void`); toast.success("Anulada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout
      title="Notas de crédito de compra"
      subtitle={`${purchaseCNs.length} notas de crédito de proveedores`}
      actions={
        <button onClick={() => setOpenPurchase(true)} data-testid="new-pcn-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
          <PlusIcon size={16} weight="bold" /> Crear nota de crédito de compra
        </button>
      }
    >
      <div className="mb-4 text-xs text-text-muted bg-card border border-border/60 rounded-md p-3" data-testid="cn-ventas-moved">
        Las notas de crédito de venta se gestionan en{" "}
        <Link to="/ventas?tab=notas-credito" className="text-agro-green hover:underline">Ventas y facturación → Notas de crédito</Link>.
      </div>
      <CNTable rows={purchaseCNs.map((n) => ({ ...n, proveedor_sub: n.subcuenta_nombre || n.proveedor_nombre }))} kind="compras"
        partyLabel="Proveedor / subcuenta" partyField="proveedor_sub"
        onView={setViewing} onVoid={voidCn} />
      {openPurchase && <PurchaseCNModal onClose={() => setOpenPurchase(false)} onSaved={() => { setOpenPurchase(false); load(); }} />}
      {viewing && <CNViewer cn={viewing} onClose={() => setViewing(null)} />}
    </Layout>
  );
}

function PurchaseCNModal({ onClose, onSaved }) {
  const [invoices, setInvoices] = useState([]);
  const [facturaId, setFacturaId] = useState("");
  const [factura, setFactura] = useState(null);
  const [tipo, setTipo] = useState("devolucion");
  const [items, setItems] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [actualizaInv, setActualizaInv] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/purchase-invoices");
      setInvoices(data.filter((i) => i.estado !== "anulada"));
    })();
  }, []);

  useEffect(() => {
    if (!facturaId) { setFactura(null); setItems([]); return; }
    (async () => {
      const { data } = await api.get(`/purchase-invoices/${facturaId}`);
      setFactura(data);
      setItems(data.items.map((it) => ({
        producto_id: it.producto_id, codigo: it.codigo, descripcion: it.descripcion,
        cantidad_factura: it.cantidad, cantidad: 0,
        // en la moneda de la factura (USD si es en dólares; el servidor convierte con la tasa de la factura)
        precio_unitario: it.precio_unitario_moneda ?? it.precio_unitario, isv_pct: it.isv_pct, descuento: 0,
      })));
    })();
  }, [facturaId]);

  const totals = useMemo(() => {
    let s15 = 0, s18 = 0, sx = 0, i15 = 0, i18 = 0, total = 0;
    items.forEach((it) => {
      const neto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) - (Number(it.descuento) || 0);
      if (neto <= 0) return;
      if (Number(it.isv_pct) === 15) { s15 += neto; i15 += neto * 0.15; total += neto * 1.15; }
      else if (Number(it.isv_pct) === 18) { s18 += neto; i18 += neto * 0.18; total += neto * 1.18; }
      else { sx += neto; total += neto; }
    });
    return { s15, s18, sx, i15, i18, total };
  }, [items]);

  const save = async () => {
    if (!facturaId) return toast.error("Selecciona una factura de proveedor");
    const itemsPayload = items.filter(it => Number(it.cantidad) > 0).map(it => ({
      producto_id: it.producto_id, codigo: it.codigo, descripcion: it.descripcion,
      cantidad: Number(it.cantidad), precio_unitario: Number(it.precio_unitario),
      descuento: Number(it.descuento) || 0, isv_pct: Number(it.isv_pct),
    }));
    if (itemsPayload.length === 0) return toast.error("Indica cantidades");
    setSaving(true);
    try {
      await api.post("/purchase-credit-notes", { purchase_invoice_id: facturaId, tipo, items: itemsPayload, motivo, actualiza_inventario: actualizaInv && tipo === "devolucion" });
      toast.success("NC compra emitida"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Nueva NC Compra" onClose={onClose}>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="col-span-2">
          <Lbl>Factura de proveedor *</Lbl>
          <select data-testid="pcn-factura" value={facturaId} onChange={(e) => setFacturaId(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
            <option value="">— Selecciona factura —</option>
            {invoices.map((i) => <option key={i.id} value={i.id}>{i.numero_interno} · {i.subcuenta_nombre || i.proveedor_nombre} · {fmtMon(i.total_moneda ?? i.total, i.moneda)}</option>)}
          </select>
        </div>
        <div>
          <Lbl>Tipo *</Lbl>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
            <option value="devolucion">Devolución al proveedor</option>
            <option value="descuento">Descuento posterior</option>
            <option value="ajuste">Ajuste</option>
          </select>
        </div>
        <div>
          <Lbl>Motivo</Lbl>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
        </div>
      </div>

      {factura && (
        <div className="bg-app border border-border rounded-md p-3 mb-3 text-xs text-text-secondary">
          <span className="text-text-muted">Proveedor:</span> <span className="text-text-primary">{factura.proveedor_nombre}</span>
          {factura.subcuenta_nombre && <> · <span className="text-text-muted">Subcuenta:</span> <b className="text-text-primary" data-testid="pcn-subcuenta">{factura.subcuenta_codigo} · {factura.subcuenta_nombre}</b></>} · 
          <span className="text-text-muted ml-2">Forma:</span> <span className="capitalize">{factura.forma_pago}</span> · 
          <span className="text-text-muted ml-2">Saldo:</span> <span className="text-agro-yellow">{fmtMon(factura.saldo_moneda ?? factura.saldo_pendiente ?? 0, factura.moneda)}</span> · 
          <span className="text-text-muted ml-2">Total:</span> <span className="text-agro-green">{fmtMon(factura.total_moneda ?? factura.total, factura.moneda)}</span>
          {factura.moneda === "USD" && <div className="mt-1">Factura en dólares: los importes de la nota van en US$ y se convierten con la tasa de la factura ({factura.tipo_cambio}); no puede exceder el saldo.</div>}
        </div>
      )}

      {items.length > 0 && (
        <div className="border border-border rounded-md overflow-hidden mb-4">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-2 pl-4">Producto</th>
                <th className="p-2 text-right">Factura</th>
                <th className="p-2 text-right">A devolver</th>
                <th className="p-2 text-right">Costo</th>
                <th className="p-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const sub = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
                return (
                  <tr key={i} className="border-t border-border/40">
                    <td className="p-2 pl-4 text-xs"><div className="font-mono text-agro-green">{it.codigo}</div><div className="text-text-secondary">{it.descripcion}</div></td>
                    <td className="p-2 text-right text-xs text-text-muted">{it.cantidad_factura}</td>
                    <td className="p-2"><input type="number" data-testid={`pcn-row-qty-${i}`} value={it.cantidad} max={it.cantidad_factura} min={0} step="0.01" onChange={(e) => { const n = [...items]; n[i].cantidad = e.target.value; setItems(n); }} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                    <td className="p-2"><input type="number" value={it.precio_unitario} step="0.01" onChange={(e) => { const n = [...items]; n[i].precio_unitario = e.target.value; setItems(n); }} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                    <td className="p-2 text-right text-xs font-medium">{fmtMon(sub, factura?.moneda)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-card border border-agro-yellow/30 rounded-md p-4 mb-4">
        <div className="flex justify-between items-baseline">
          <span className="text-text-secondary text-xs uppercase tracking-wider">Total NC compra</span>
          <span className="font-heading text-2xl font-semibold text-agro-yellow" data-testid="pcn-total">{fmtMon(totals.total, factura?.moneda)}</span>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
        <button onClick={save} disabled={saving || totals.total <= 0} data-testid="pcn-submit" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-5 py-2 text-sm disabled:opacity-50">{saving ? "Guardando…" : "Emitir NC Compra"}</button>
      </div>
    </Modal>
  );
}

